import { HttpErrorResponse } from '@angular/common/http';
import { Component, NgZone, OnInit } from '@angular/core';
import { PendoService } from 'analytics-lib';
import { AwsRumService } from 'analytics-lib';
import { GoogleAnalyticsService } from 'analytics-lib';
import { messageInOut } from 'animations-lib';
import { IMessage, MessageLevel, MessageNotificationService } from 'message-notification-service-lib';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { Identity, IdentityStorage } from 'storage-service-lib';
import { manifestVersion } from '../environments/build.version';
import { environment } from '../environments/environment';
import { FieldInsertEvent, FieldInsertService, SignatureFieldInsertEvent } from './components/adc-migration/field-insert.service';
import { DocumentSenderFieldProviderService } from './components/field-selection/document-sender/document-sender-field-provider.service';
import { FieldMetaTypeGuards } from './interfaces/FieldMetaTypeGuards';
import { EntityTypeLabelPair, IEntityMeta, IFieldMeta } from './interfaces/imetadata';
import { ISignatureFieldEvent } from './interfaces/signature';
import { Adv2ConfigService } from './services/adv2-config/adv2-config.service';
import { AppEventsControllerService } from './services/app-events-controller.service';
import { ApplicationStoreService } from './services/application-store.service';
import { AuthServiceFactory } from './services/auth/authservice.factory';
import { ContentControlToFieldMapService } from './services/content-control-to-field-map.service';
import { ControllerService } from './services/controller.service';
import { ModelCorruptMonitor } from './services/document-monitors/model-corrupt-monitor';
import { SignatureControllerService } from './services/esignature/signature-controller.service';
import { LoggingService } from './services/logging.service';
import { ServiceProviderService } from './services/service.provider.service';
import { UpgradeService } from './services/upgrade.service';
import { DateFormatOptions, FieldConfig, StringFormatOptions } from './utils/fieldconfig';
import { FieldConfigHelper } from './utils/fieldconfighelper';
import { FieldContext } from './utils/fieldcontext';
import { FieldType } from './utils/fieldtype';
import { templateContext } from './utils/template-context';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [messageInOut],
  standalone: false,
})
export class AppComponent implements OnInit {
  title = 'Kyloe AwesomeDocs';
  showLogin = true;
  message: IMessage | undefined = undefined;
  corruptModelMessage = '';
  showEntitySelection = false;
  identity: Identity | null | undefined;
  signatureProviderName: string | undefined = undefined;
  entityMeta: IEntityMeta | undefined; // TODO: BW - this isn't right if fields come from other namespaces

  private config: FieldConfigHelper | undefined = undefined;

  protected readonly MessageLevel = MessageLevel;

  constructor(
    private applicationStoreService: ApplicationStoreService,
    private appEventsControllerService: AppEventsControllerService,
    private serviceProviderService: ServiceProviderService,
    private controllerService: ControllerService,
    private messageNotificationService: MessageNotificationService,
    private authServiceFactory: AuthServiceFactory,
    private ngZone: NgZone,
    private modelCorruptMonitor: ModelCorruptMonitor,
    private adv2ConfigService: Adv2ConfigService,
    private signatureControllerService: SignatureControllerService,
    private awsRumService: AwsRumService,
    private gaService: GoogleAnalyticsService,
    private pendoService: PendoService,
    private documentSenderFieldProviderService: DocumentSenderFieldProviderService,
    private identityStorage: IdentityStorage,
    private contentControlToFieldMapService: ContentControlToFieldMapService,
    private upgradeService: UpgradeService,
    private loggingService: LoggingService,
    private fieldInsertService: FieldInsertService
  ) {}

  async ngOnInit() {
    this.messageNotificationService.message$.subscribe((msg) => {
      this.ngZone.run(() => (this.message = msg.message));
    });

    this.modelCorruptMonitor.corruptEvent$.subscribe((corruptCount) => {
      this.ngZone.run(() => {
        this.corruptModelMessage = `Model corrupted and repaired ${corruptCount} times.`;
      });
    });

    this.fieldInsertService.fieldInsertedEvents.subscribe((event) => this.fieldInsertEventHandler(event));
    this.fieldInsertService.signatureFieldInsertedEvents.subscribe((event) => this.signatureFieldInsertEventHandler(event));

    // if (!environment.isDev) {
    //   // Check if there is a new version available and refresh if there is
    //   const latestVersion = await this.upgradeService.getLatestWordAddinVersion();
    //   if (latestVersion && latestVersion !== manifestVersion) {
    //     this.loggingService.log('AppComponent', 'ngOnInit', `Refreshing to update to the new version: ${latestVersion}`);
    //     setTimeout(() => this.reloadPage(), 2000);
    //     return;
    //   }
    // }

    const latestVersion = await this.upgradeService.getLatestWordAddinVersion();
    console.log(`Version: ${manifestVersion}, Latest version: ${latestVersion}`);

    await this.runUpgrades();

    this.loginOrLoad();

    this.initialiseAnalytics();
  }

  loginSuccess(identityInfo: Identity): void {
    this.identityStorage.put(identityInfo);
    this.identity = identityInfo;
    this.ngZone.run(() => (this.showLogin = false));
    this.updateCurrentVersion();
    this.loadConfig(identityInfo.token);
    this.showEntitySelectionOrLoadFields(identityInfo);
  }

  async targetEntitySelected(entity: EntityTypeLabelPair): Promise<void> {
    this.showEntitySelection = false;
    await this.applicationStoreService.putNamespaceEntity(this.identity!.namespace, entity.entity);
    this.loadFieldMeta(this.identity!.namespace, this.identity!.token, entity.entity);
  }

  async fieldInserted(event: {
    entity: EntityTypeLabelPair;
    field: IFieldMeta;
    options?: { date: DateFormatOptions; default: string };
  }): Promise<void> {
    // const entity: EntityTypeLabelPair = this.entityMeta!;
    const entity = event.entity;
    const field = event.field;

    return Word.run(async (context) => {
      try {
        let dateFormatOptions = undefined;
        if (field.type === 'date') {
          dateFormatOptions = this.config!.getDateFormatOptions(event.options?.date);
        }

        let integerFormat = undefined;
        if (field.type === 'integer') {
          integerFormat = this.config!.getIntegerFormat();
        }

        let decimalFormat = undefined;
        if (field.type === 'double') {
          decimalFormat = this.config!.getDecimalFormat();
        }

        const sortOption = this.config!.getSortOption(field);

        const useDisplayValue = FieldMetaTypeGuards.isBullhorn(field) && field.options ? true : undefined;
        const fieldType = useDisplayValue ? FieldType.STRING : field.type;
        const defaultEmptyValue = this.config!.getDefaultEmptyValue(field.path, field.type, event.options?.default);

        let stringFormat = undefined;
        // will be system defaults when useDisplayValue is true
        // booleans are treated as strings, so they get the string formatting options too
        if (fieldType === FieldType.STRING || fieldType === FieldType.BOOLEAN) {
          stringFormat = this.config!.getStringFormat(field);
        }

        const fieldContext = new FieldContext(
          this.entityMeta?.namespace!,
          entity,
          field,
          undefined,
          dateFormatOptions,
          integerFormat,
          decimalFormat,
          sortOption,
          undefined,
          defaultEmptyValue,
          stringFormat,
          useDisplayValue
        );

        await this.controllerService.insert(context, fieldContext);
        await context.sync();
      } catch (err: any) {
        this.loggingService.error('AppComponent', 'fieldInserted', {
          message: 'Failed to insert field.',
          error: err,
        });
        this.messageNotificationService.error('Failed to insert field.', err);
      }
    });
  }

  fieldUpdated(event: { entity: EntityTypeLabelPair; field: IFieldMeta; fieldConfig: FieldConfig }) {
    if (event.field) {
      // TODO: BW, MT - this needs to make sure it sets the defaults?

      Word.run(async (context) => {
        try {
          const useDisplayValue = event.fieldConfig.useDisplayValue ? true : undefined; // only makes sense for some fields
          // const fieldType = useDisplayValue ? FieldType.STRING : event.field.type;
          const defaultEmptyValue = this.config!.getDefaultEmptyValue(
            event.field.path,
            event.field.type,
            event.fieldConfig.defaultEmptyValue
          );
          // if using display value then there should be no formatting options, so it will fall back to system defaults
          const stringFormat = this.ensureStringFormatOptions(
            event.field,
            useDisplayValue ? this.config!.getStringFormat(event.field) : event.fieldConfig.stringFormat
          );

          const fieldContext = new FieldContext(
            this.entityMeta?.namespace!,
            event.entity,
            event.field,
            event.fieldConfig.conditions,
            event.fieldConfig.dateFormatOptions,
            event.fieldConfig.integerFormat,
            event.fieldConfig.decimalFormat,
            event.fieldConfig.sortOption,
            event.fieldConfig.limit,
            defaultEmptyValue,
            stringFormat,
            useDisplayValue
          );
          await this.controllerService.update(context, fieldContext);
          await context.sync();
        } catch (err: any) {
          this.loggingService.error('AppComponent', 'fieldUpdated', {
            message: 'Failed to update field.',
            error: err,
          });
          this.messageNotificationService.error('Failed to update field.', err);
        }
      });
    }
  }

  async signatureFieldClicked(event: ISignatureFieldEvent) {
    console.debug('Signature field clicked', event);

    return Word.run(async (context) => {
      try {
        await this.signatureControllerService.insert(context, event);
        await context.sync();
      } catch (err: any) {
        this.loggingService.error('AppComponent', 'signatureFieldClicked', {
          message: 'Failed to insert signature field.',
          error: err,
        });
        this.messageNotificationService.error('Failed to insert signature field.', err);
      }
    });
  }

  logout(): void {
    this.identityStorage.clear();
    this.ngZone.run(() => {
      this.showLogin = true;
      this.entityMeta = undefined;
    });
  }

  private async runUpgrades(): Promise<void> {
    console.debug('Running upgrades.');
    this.showLogin = false;
    const waitMessageId = setTimeout(() => {
      this.messageNotificationService.info('Your template requires an upgrade. Please wait.');
    }, 2000);

    try {
      // server upgrade first
      console.time('Server Upgrade');
      await this.upgradeService.run();
      console.timeEnd('Server Upgrade');

      // internal upgrade calls go here
      await this.contentControlToFieldMapService.upgrade();

      console.debug('Upgrades complete.');

      this.message = undefined;
      this.showLogin = true;
    } catch (e: HttpErrorResponse | any) {
      const error = e?.error ? e?.error?.message : e;
      this.messageNotificationService.error('Failure during template upgrade process.', error);
    } finally {
      clearTimeout(waitMessageId);
    }
  }

  private ensureStringFormatOptions(fieldMeta: IFieldMeta, stringFormatOptions: StringFormatOptions | undefined) {
    if (!stringFormatOptions) {
      console.warn('Old template? Missing string format options.');
      stringFormatOptions = this.config!.getStringFormat(fieldMeta);
    }
    return stringFormatOptions;
  }

  private initialiseAnalytics(): void {
    this.awsRumService.init();
    this.gaService.init();
    this.pendoService.initPendoScript(environment.pendoAPIKey);
  }

  private loginOrLoad(): void {
    this.identity = this.identityStorage.get();
    const validIdentity = this.validIdentity(this.identity);
    if (!validIdentity) {
      this.showLogin = true;
      return;
    }

    this.showLogin = false;
    this.updateCurrentVersion();
    this.loadConfig(this.identity!.token);
    this.showEntitySelectionOrLoadFields(this.identity!);
  }

  private updateCurrentVersion(): void {
    (async () => {
      try {
        const addInInfo = this.applicationStoreService.getAddInInfo();
        addInInfo.currentVersion = environment.version;
        this.loggingService.log('AppComponent', 'updateCurrentVersion', `Setting current version to ${addInInfo.currentVersion}`);
        await this.applicationStoreService.set('kp-add-in-info', addInInfo);
      } catch (error) {
        this.loggingService.error('AppComponent', 'updateCurrentVersion', {
          message: 'Failed to set current version.',
          error: error,
        });
      }
    })();
  }

  private showEntitySelectionOrLoadFields(identity: Identity): void {
    this.updateLegacyTemplate(identity.namespace).then((_) => {
      let entity: string;
      const ok = (entityName: string) => (entity = entityName);
      if (this.tryGetTargetEntity(identity.namespace, ok)) {
        this.loadFieldMeta(identity.namespace, identity.token, entity!);
      } else {
        this.ngZone.run(() => (this.showEntitySelection = true));
      }
    });
  }

  private loadConfig(token: string): void {
    this.adv2ConfigService.getConfig(token).subscribe({
      next: (config) => {
        console.debug(
          `dateFormat: ${config.dateFormat}, emptyFields: ${JSON.stringify(config.emptyFields)}, signatureProvider: ${
            config.signatureProviderName
          }`
        );
        this.config = new FieldConfigHelper(config);
        this.signatureProviderName = config.signatureProviderName;
      },
      error: (err) => {
        this.loggingService.error('AppComponent', 'loadConfig', {
          message: 'Failed to load config',
          error: err,
        });
        this.messageNotificationService.error('Failed to load configuration.');
      },
    });
  }

  private validIdentity(identity: Identity | null): boolean {
    if (!identity) {
      return false;
    }
    const authService = this.authServiceFactory.getAuthService(identity.namespace);
    return authService.validToken(identity.token);
  }

  private async updateLegacyTemplate(namespace: string): Promise<void> {
    const isLegacyTemplate = !this.applicationStoreService.hasNamespaceEntities(namespace);
    if (isLegacyTemplate) {
      const entityName = await this.controllerService.getEntityForNamespace(namespace);
      if (entityName) {
        this.loggingService.log(
          'AppComponent',
          'updateLegacyTemplate',
          `Legacy template detected with existing '${entityName}' fields. Saving to store.`
        );
        await this.applicationStoreService.putNamespaceEntity(namespace, entityName);
      }
    }
  }

  private tryGetTargetEntity(namespace: string, out: (entityName: string) => void): boolean {
    const entities = this.applicationStoreService.getNamespaceEntities(namespace);
    if (!entities || entities.length === 0) {
      return false;
    }

    // TODO: we need (interface + service) to interpret which is the 'target' entity
    out(entities[0]);
    return true;
  }

  private loadFieldMeta(namespace: string, token: string, entityName: string): void {
    // Salesforce must lazy-load associated metadata; start with depth 0 and fetch relationships on demand.
    const maxDepth = namespace === 'salesforce' ? 0 : undefined;
    const metaDataService = this.serviceProviderService.getMetaDataService(namespace);
    const subscription: Subscription = metaDataService
      .getEntityFields(token, entityName, undefined, maxDepth, namespace)
      .pipe(map((metaData) => metaData.entities.find((e) => e.entity === entityName)!))
      .subscribe({
        next: (meta) => {
          this.ngZone.run(() => {
            this.entityMeta = meta;
            console.debug('ENTITY META', meta);
            templateContext.setTargetEntity(this.entityMeta);
            this.appEventsControllerService.appReady();
          });
        },
        error: (error) => {
          let details = undefined;
          console.error(error);
          if (error instanceof HttpErrorResponse) {
            details = error.error ? error.error.message ?? error.statusText : error.statusText;
          }
          this.messageNotificationService.error('Failed to load field information.', details);
        },
        complete: () => subscription.unsubscribe(),
      });

    this.documentSenderFieldProviderService.initialise(namespace, token);
  }

  private fieldInsertEventHandler(event: FieldInsertEvent): void {
    (async () => {
      await this.fieldInserted(event);
      event.resolve(null);
    })();
  }

  private signatureFieldInsertEventHandler(event: SignatureFieldInsertEvent): void {
    (async () => {
      await this.signatureFieldClicked(event.fieldEvent);
      event.resolve(null);
    })();
  }

  protected reloadPage(): void {
    window.location.reload();
  }
}
